import type { FC } from 'react';
import { useLocation } from 'react-router-dom';
import {
  buildPageLoaderBranding,
  buildInitialAppLoadPageLoaderMessages,
  CLIENTS_PAGE_LOADER_MESSAGES,
  DASHBOARD_PAGE_LOADER_MESSAGES,
  getAdvisorNameFromUser,
  PLANNER_PAGE_LOADER_MESSAGES,
  WavvestPageLoader,
} from '@/common/components/page-loader';
import {
  type InitialAppLoadDestination,
  getInitialAppLoadAdvisorName,
  getInitialAppLoadBranding,
  getInitialAppLoadDestination,
  getInitialAppLoadStartedAt,
  getInitialAppLoadSuppressAdvisorName,
  hasInitialAppLoadState,
} from '@/routes/initial-app-load-state';
import { useAppSelector } from '@/store/hooks';
import { selectProfileData, selectUser } from '@/store/features/userinfo/userinfoSlice';

const getDestinationForPathname = (pathname: string): InitialAppLoadDestination | undefined => {
  if (pathname.startsWith('/home/dashboard')) {
    return 'dashboard';
  }

  if (pathname.startsWith('/home/planning')) {
    return 'planner';
  }

  if (pathname.startsWith('/home/clients')) {
    return 'clients';
  }

  return undefined;
};

const getMessagesForLocation = (
  pathname: string,
  state: unknown,
  advisorName: string | undefined,
  destination: InitialAppLoadDestination | undefined,
): ReadonlyArray<string> | undefined => {
  if (hasInitialAppLoadState(state)) {
    return buildInitialAppLoadPageLoaderMessages({
      advisorName,
      destination,
    });
  }

  if (pathname.startsWith('/home/dashboard')) {
    return DASHBOARD_PAGE_LOADER_MESSAGES;
  }

  if (pathname.startsWith('/home/planning')) {
    return PLANNER_PAGE_LOADER_MESSAGES;
  }

  if (pathname.startsWith('/home/clients')) {
    return CLIENTS_PAGE_LOADER_MESSAGES;
  }

  return undefined;
};

export const InitialAppLoadPageLoader: FC = () => {
  const location = useLocation();
  const user = useAppSelector(selectUser);
  const profileData = useAppSelector(selectProfileData);
  const isInitialAppLoad = hasInitialAppLoadState(location.state);
  const suppressAdvisorName = getInitialAppLoadSuppressAdvisorName(location.state);
  const advisorName = suppressAdvisorName
    ? undefined
    : (getInitialAppLoadAdvisorName(location.state) ?? getAdvisorNameFromUser(user));
  const destination = isInitialAppLoad
    ? (getInitialAppLoadDestination(location.state) ?? getDestinationForPathname(location.pathname))
    : getDestinationForPathname(location.pathname);
  const carriedBranding = getInitialAppLoadBranding(location.state);
  const profileBranding = buildPageLoaderBranding(profileData);
  const branding = isInitialAppLoad
    ? (carriedBranding ?? profileBranding)
    : (profileBranding ?? carriedBranding);

  return (
    <WavvestPageLoader
      animationStartedAt={getInitialAppLoadStartedAt(location.state)}
      branding={branding}
      destination={destination}
      messages={getMessagesForLocation(location.pathname, location.state, advisorName, destination)}
      showFallbackBranding={false}
    />
  );
};
